import RequestService from 'common/services/api/RequestService';
import ResponseHandler from 'common/services/api/ResponseHandler';
import { configureCommonApiService } from 'common/services/api/commonApiService';
import {
    initializeCommonNavigationService,
} from 'common/services/commonNavigationService';
import i18nService from 'common/services/i18nService';
import { Alert, AlertLevel } from 'common/components/notifications/types';
import { pushAlert } from 'common/components/notifications/actions';
import { configureHistoryService } from 'common/services/HistoryService';
import urlService from 'common/services/api/urlService';
import { initializeLanguageChangeService } from 'common/features/player/services/languageChangeService';
import { configureWorkspaceSchemeService } from 'common/services/commonWorkspaceSchemeService';
import { configureSessionSagas } from 'common/store/session/sagas';
import { initializeCommonSearchHitService } from 'common/services/search/commonSearchHitService';
import { initializeCommonFilteringService } from 'common/services/commonFilteringService';
import { initializedCommonSearchQueryBuilderService } from 'common/services/search/commonSearchQueryBuilder';
import { initializeFooterNavigationService } from 'common/features/footer/services/navigationService';
import { initializeCommonPermissionService } from 'common/services/permissions/commonPermissionService';
import { initializeCommonRoutingService } from 'common/services/commonRoutingService';
import pendoAnalyticsService from 'common/services/pendoAnalyticsService';
import storeManager from './store';
import { RootState } from './store/rootReducer';

import navigationListService from './services/navigationListService';
import { configureApiService } from './services/api/apiService';
import workspaceLoadingService from './services/workspaceLoadingService';
import awsIOTService from './services/AWSIOTService';
import filteringService from './services/filteringService';
import searchQueryBuilderService from './services/search/searchQueryBuilderService';
import searchHitService from './services/search/searchHitService';
import { getCurrentUserPrivileges, permissionService } from './services/permissions/PermissionService';
import routingService from './services/routingService';
import { supportedLanguages } from './services/i18nService';
import notificationService from './services/notificationService';

const resources = require('../i18n/index.ts');

const responseHandler = new ResponseHandler({
    handleAlert: (alert: Alert) => {
        const { key } = alert;
        const alertNotifications = notificationService.predefinedAlertNotifications;

        let newAlert: Alert = { ...alert };
        let notRemove = false;

        if (key === 'invalidFileExtension') {
            newAlert = { ...newAlert, ...alertNotifications.fileError('fileTypeNotSupported') };
            notRemove = true;
        }
        storeManager.dispatch(pushAlert(newAlert, notRemove));
    },

    handleNotLoggedIn: () => {
        sessionStorage.setItem('requestedGradingUrl', location.href.split(location.host).pop());
        navigationListService.redirectToLogin();
    },

    handleForbidden: (): void => {
        storeManager.dispatch(pushAlert({
            key: 'grading.impersonation.viewonly.error.title',
            info: 'grading.impersonation.viewonly.error.message',
            level: AlertLevel.ERROR,
            payload: {},
        }));
    },

    checkIsViewOnlyImpersonation: (): boolean => {
        return storeManager.getState().session?.impersonationType === 'VIEW_ONLY';
    },
});

const getCsrfToken = () => (storeManager.getState() as RootState).session.sessionInfo.csrfToken;
const requestService = new RequestService(responseHandler, getCsrfToken, (path: string) => urlService.prependDomain(path));

const getUiOverrideLang = (): string => (storeManager.getState() as RootState).assessment.uiOverrideLang;

const privilegesGetter = {
    getCurrentUserPrivileges,
};

const initServices = () => {
    configureHistoryService('/grading/');
    storeManager.createStore();
    initializeCommonPermissionService(storeManager, permissionService);
    initializeFooterNavigationService(storeManager);
    initializeLanguageChangeService(storeManager);
    initializeCommonNavigationService(storeManager, navigationListService, searchHitService);
    initializeCommonFilteringService(filteringService);
    initializeCommonSearchHitService(searchHitService);
    initializedCommonSearchQueryBuilderService(searchQueryBuilderService);
    initializeCommonRoutingService(routingService);
    configureCommonApiService(requestService, workspaceLoadingService);
    configureApiService(requestService);
    configureSessionSagas(awsIOTService);
    configureWorkspaceSchemeService(privilegesGetter);
    i18nService.initI18n(resources, supportedLanguages, getUiOverrideLang());
    pendoAnalyticsService.initializePendoAnalytics();
};

export default initServices;
